import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable } from 'rxjs-compat';
import { map, startWith } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';

import { PayerAddressBookService } from 'src/app/Services/AdminConfigurations/payer-address-book.service';
import { SharedService } from 'src/app/Sharedservice/SharedService';
import { IPayerAddressBook } from 'src/app/models/IPayerAddressBook';
import { ICityDetails } from 'src/app/models/ICityDetails';

export interface ClaimAddressDialogData {
  insuranceCarrierId: number;
}

// One-off address used only as a Classification Category's Claims Address override - never
// listed in the Payer Address Book, so it is always saved with AddressType = 'CLAIM_ADDRESS'
// (exempted server-side from the one-active-address-per-type rule the rest of the book follows).
@Component({
  selector: 'app-claim-address-dialog',
  templateUrl: './claim-address-dialog.component.html',
  styleUrls: ['./claim-address-dialog.component.css']
})
export class ClaimAddressDialogComponent implements OnInit {

  addressForm: FormGroup;

  cityTypeOptions: Observable<ICityDetails[]>;
  public lstCityInfo: ICityDetails[] = [];
  zipCodevalue: number = 0;

  constructor(
    private formBuilder: FormBuilder,
    public dialogRef: MatDialogRef<ClaimAddressDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ClaimAddressDialogData,
    private payerAddressBookService: PayerAddressBookService,
    private sharedService: SharedService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService) { }

  ngOnInit() {
    this.addressForm = this.formBuilder.group({
      address1: ['', Validators.required],
      address2: [''],
      postalCode: ['', Validators.required],
      city: ['', Validators.required],
      state: ['', Validators.required],
      effectiveDate: [this.todayAsDate(), Validators.required],
      expirationDate: ['']
    });
  }

  get f() { return this.addressForm.controls; }

  private todayAsDate(): Date {
    return new Date();
  }

  // Mirrors payer-category.component.ts's compareAddressDates() - live (dateChange) guard on
  // the Effective/Expiration Date matDatepicker pair: if Expiration is set and falls before
  // Effective, clear it and warn immediately rather than waiting for the save-time check.
  compareAddressDates(form: FormGroup): void {
    const expirationDate = form.value.expirationDate;
    const effectiveDate = form.value.effectiveDate;
    if (expirationDate && effectiveDate && new Date(expirationDate) < new Date(effectiveDate)) {
      form.patchValue({ expirationDate: '' });
      this.toastr.error('Expiration date can\'t be before Effective date');
    }
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }

  onZipKeyUp(event: any) {
    const keyCode = event.keyCode;
    const excludedKeys = [9, 16];
    if (keyCode === 40 || keyCode === 13) {
      return;
    }
    if (!excludedKeys.includes(keyCode)) {
      const zip = event.target.value;
      if (zip.length === 5) {
        this.getZipCodeInfo(zip);
      } else {
        this.lstCityInfo = [];
        this.cityTypeFilter();
        this.addressForm.patchValue({ city: '', state: '' });
      }
    }
  }

  getZipCodeInfo(id: number) {
    if (id == null) {
      return;
    }
    this.zipCodevalue = id;
    this.sharedService.getZipCodeDetailsAutoComplete(id).subscribe(
      (city: ICityDetails[]) => {
        this.lstCityInfo = city;
        if (this.lstCityInfo.length === 0) {
          this.toastr.error('Invalid Zip code');
          this.addressForm.patchValue({ postalCode: '' });
        } else if (this.lstCityInfo.length > 1) {
          this.cityTypeFilter();
        } else {
          this.lstCityInfo = [];
          this.addressForm.patchValue({
            city: city[0].cityName,
            state: city[0].stateName
          });
        }
      },
      (err: any) => console.log(err)
    );
  }

  cityTypeFilter() {
    this.cityTypeOptions = this.addressForm.get('postalCode')!.valueChanges
      .pipe(
        startWith(''),
        map(value => this.filterCityByZip(value))
      );
  }

  private filterCityByZip(name: string): ICityDetails[] {
    const filterValue = name ? name.toString().toLowerCase() : '';
    return this.lstCityInfo.filter(option => option.cityName.toLowerCase().indexOf(filterValue) === 0);
  }

  onClickZipCode(city: ICityDetails) {
    this.addressForm.patchValue({
      city: city.cityName,
      state: city.stateName,
      postalCode: this.zipCodevalue
    });
  }

  focusOutZipCode() {
    const zip = this.addressForm.value.postalCode;
    if (zip && zip.length !== 5) {
      this.toastr.error('Invalid Zip Code');
      this.addressForm.patchValue({ postalCode: '' });
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  save() {
    if (this.addressForm.invalid) {
      this.toastr.warning('Address Line 1, City, State, and ZIP are all required.');
      return;
    }

    const effectiveDate = this.addressForm.value.effectiveDate;
    const expirationDate = this.addressForm.value.expirationDate || null;

    if (expirationDate && expirationDate < effectiveDate) {
      this.toastr.error('Expiration Date cannot be before the Effective Date.');
      return;
    }

    const newAddress: IPayerAddressBook = {
      id: null,
      facilityId: Number(sessionStorage.getItem('FacilityId')),
      insuranceCarrierId: this.data.insuranceCarrierId,
      addressType: 'CLAIM_ADDRESS',
      address1: this.addressForm.value.address1,
      address2: this.addressForm.value.address2,
      city: this.addressForm.value.city,
      state: this.addressForm.value.state,
      postalCode: this.addressForm.value.postalCode,
      effectiveDate: effectiveDate,
      expirationDate: expirationDate,
      status: 'ACTIVE',
      supersededByBookId: null,
      createdBy: encodeURIComponent(sessionStorage.getItem('userId')).toString()
    };

    this.spinner.show();
    this.payerAddressBookService.addPayerAddress(newAddress).subscribe(result => {
      this.toastr.success(result.message);
      this.spinner.hide();
      this.dialogRef.close({ newBookId: result.newBookId });
    }, (err) => {
      this.toastr.error(err.error || 'Failed to add address.');
      this.spinner.hide();
    });
  }
}
